import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Consent categories
export const CONSENT_CATEGORIES = {
  necessary: 'necessary',      // Always required
  analytics: 'analytics',      // Google Analytics, PostHog
  marketing: 'marketing',      // Ads, retargeting
  preferences: 'preferences',  // Language, theme preferences
};

// Default consent state (denied by default for GDPR compliance)
const DEFAULT_CONSENT = {
  necessary: true,      // Always true - can't be disabled
  analytics: false,
  marketing: false,
  preferences: false,
  consentGiven: false,  // Has user made a choice?
  consentDate: null,
  consentVersion: '1.0',
};

const CONSENT_STORAGE_KEY = 'tradehub24_cookie_consent';
const CONSENT_VERSION = '1.0';

const ConsentContext = createContext(null);

export const useConsent = () => {
  const context = useContext(ConsentContext);
  if (!context) {
    throw new Error('useConsent must be used within a ConsentProvider');
  }
  return context;
};

export const ConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(DEFAULT_CONSENT);
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  // Load consent from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // Check if consent version matches
        if (parsed.consentVersion === CONSENT_VERSION && parsed.consentGiven) {
          setConsent(parsed);
          updateGoogleConsent(parsed);
          updateAnalytics(parsed);
        } else {
          // Version mismatch or no consent - show banner
          setShowBanner(true);
        }
      } catch (e) {
        setShowBanner(true);
      }
    } else {
      // No stored consent - show banner after short delay
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Update Google Consent Mode v2
  const updateGoogleConsent = useCallback((consentState) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: consentState.analytics ? 'granted' : 'denied',
        ad_storage: consentState.marketing ? 'granted' : 'denied',
        ad_user_data: consentState.marketing ? 'granted' : 'denied',
        ad_personalization: consentState.marketing ? 'granted' : 'denied',
        functionality_storage: consentState.preferences ? 'granted' : 'denied',
        personalization_storage: consentState.preferences ? 'granted' : 'denied',
        security_storage: 'granted', // Always granted for necessary cookies
      });
      console.log('[CMP] Google Consent Mode updated:', consentState);
    }
  }, []);

  // Update analytics based on consent
  const updateAnalytics = useCallback((consentState) => {
    // PostHog
    if (typeof window !== 'undefined' && window.posthog) {
      if (consentState.analytics) {
        window.posthog.opt_in_capturing();
        console.log('[CMP] PostHog opted in');
      } else {
        window.posthog.opt_out_capturing();
        console.log('[CMP] PostHog opted out');
      }
    }
  }, []);

  // Save consent to localStorage and update services
  const saveConsent = useCallback((newConsent) => {
    const consentToSave = {
      ...newConsent,
      consentGiven: true,
      consentDate: new Date().toISOString(),
      consentVersion: CONSENT_VERSION,
    };
    
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consentToSave));
    setConsent(consentToSave);
    updateGoogleConsent(consentToSave);
    updateAnalytics(consentToSave);
    setShowBanner(false);
    setShowPreferences(false);
    
    console.log('[CMP] Consent saved:', consentToSave);
  }, [updateGoogleConsent, updateAnalytics]);

  // Accept all cookies
  const acceptAll = useCallback(() => {
    saveConsent({
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    });
  }, [saveConsent]);

  // Reject all (except necessary)
  const rejectAll = useCallback(() => {
    saveConsent({
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
    });
  }, [saveConsent]);

  // Save custom preferences
  const savePreferences = useCallback((preferences) => {
    saveConsent({
      necessary: true, // Always true
      analytics: preferences.analytics || false,
      marketing: preferences.marketing || false,
      preferences: preferences.preferences || false,
    });
  }, [saveConsent]);

  // Open preferences modal
  const openPreferences = useCallback(() => {
    setShowPreferences(true);
  }, []);

  // Close preferences modal
  const closePreferences = useCallback(() => {
    setShowPreferences(false);
  }, []);

  // Check if a specific category is consented
  const hasConsent = useCallback((category) => {
    return consent[category] === true;
  }, [consent]);

  const value = {
    consent,
    showBanner,
    showPreferences,
    acceptAll,
    rejectAll,
    savePreferences,
    openPreferences,
    closePreferences,
    hasConsent,
    setShowBanner,
  };

  return (
    <ConsentContext.Provider value={value}>
      {children}
    </ConsentContext.Provider>
  );
};

export default ConsentContext;
